/**
 * Cassanova API Endpoints
 *
 * Defines API endpoint functions for menu data retrieval.
 * All functions use the configured API client with automatic authentication.
 *
 * @module cassanova/endpoints
 */

import apiClient from './apiClient';
import {SALES_POINT_ID} from './config';

/**
 * Get all menu categories
 *
 * Fetches categories enabled for self-order channel from the specified sales point.
 *
 * @returns {Promise<Object>} Response object with categories array and totalCount
 * @throws {Object} Categorized error object on failure
 *
 * @example
 * const result = await getCategories();
 * console.log(result.categories); // Array of category objects
 * console.log(result.totalCount); // Total number of categories
 */
export async function getCategories() {
    try {
        if (process.env.NODE_ENV !== 'production') {
            console.log('[Endpoints] Fetching categories...');
        }

        const response = await apiClient.get('/categories', {
            params: {
                start: 0,
                limit: 50,
                idsSalesPoint: `[${SALES_POINT_ID}]`,
                enabledForChannels: '["SELF_ORDER"]',
                itemListVisibility: 'true',
            },
        });

        if (process.env.NODE_ENV !== 'production') {
            console.log('[Endpoints] Categories fetched:', response.data.totalCount);
        }

        return response.data;
    } catch (error) {
        console.error('[Endpoints] Failed to fetch categories:', error);
        throw error;
    }
}

/**
 * Get products with optional category filtering
 *
 * Fetches products from the specified sales point, optionally filtered by category.
 * Supports pagination with start and limit parameters.
 *
 * @param {string|null} categoryId - Optional category ID to filter products
 * @param {number} start - Starting index for pagination (default: 0)
 * @param {number} limit - Maximum number of products to return (default: 50)
 * @returns {Promise<Object>} Response object with products array and totalCount
 * @throws {Object} Categorized error object on failure
 *
 * @example
 * // Get all products (first 50)
 * const allProducts = await getProducts();
 *
 * @example
 * // Get products from specific category
 * const categoryProducts = await getProducts('category-123');
 *
 * @example
 * // Get products with pagination
 * const page2 = await getProducts(null, 50, 50);
 */
export async function getProducts(categoryId = null, start = 0, limit = 50) {
    try {
        if (process.env.NODE_ENV !== 'production') {
            console.log('[Endpoints] Fetching products:', {categoryId, start, limit});
        }

        // Build query parameters
        const params = {
            start,
            limit,
            idsSalesPoint: `[${SALES_POINT_ID}]`,
            enabledForChannels: '["SELF_ORDER"]',
            itemListVisibility: 'true',
        };

        // Add category filter if provided
        if (categoryId) {
            params.idsCategory = `["${categoryId}"]`;
        }

        const response = await apiClient.get('/products', {params});

        if (process.env.NODE_ENV !== 'production') {
            console.log('[Endpoints] Products fetched:', response.data.totalCount);
        }

        return response.data;
    } catch (error) {
        console.error('[Endpoints] Failed to fetch products:', error);
        throw error;
    }
}

/**
 * Get all products from all categories
 *
 * Fetches all products using pagination to retrieve complete dataset.
 * Makes multiple API calls if necessary to fetch all products.
 *
 * @param {number} pageSize - Number of products to fetch per request (default: 100)
 * @returns {Promise<Object>} Response object with all products array and totalCount
 * @throws {Object} Categorized error object on failure
 *
 * @example
 * const result = await getAllProducts();
 * console.log(result.products); // Array of all product objects
 * console.log(result.totalCount); // Total number of products
 */
export async function getAllProducts(pageSize = 100) {
    try {
        if (process.env.NODE_ENV !== 'production') {
            console.log('[Endpoints] Fetching all products...');
        }

        const allProducts = [];
        let start = 0;
        let hasMore = true;
        let totalCount = 0;

        // Fetch products in batches until all are retrieved
        while (hasMore) {
            const response = await apiClient.get('/products', {
                params: {
                    start,
                    limit: pageSize,
                    idsSalesPoint: `[${SALES_POINT_ID}]`,
                    enabledForChannels: '["SELF_ORDER"]',
                    itemListVisibility: 'true',
                },
            });

            const {products = [], totalCount: count = 0} = response.data;

            // Update total count from first response
            if (start === 0) {
                totalCount = count;
            }

            // Add products to collection
            allProducts.push(...products);

            if (process.env.NODE_ENV !== 'production') {
                console.log(`[Endpoints] Fetched ${products.length} products (${allProducts.length}/${totalCount})`);
            }

            // Check if there are more products to fetch
            if (products.length < pageSize || allProducts.length >= totalCount) {
                hasMore = false;
            } else {
                start += pageSize;
            }
        }

        if (process.env.NODE_ENV !== 'production') {
            console.log('[Endpoints] All products fetched:', allProducts.length);
        }

        return {
            products: allProducts,
            totalCount: allProducts.length,
        };
    } catch (error) {
        console.error('[Endpoints] Failed to fetch all products:', error);
        throw error;
    }
}

/**
 * Get product by ID
 *
 * Fetches a single product by its ID.
 * Note: This is a convenience function that filters products by ID.
 *
 * @param {string} productId - Product ID to fetch
 * @returns {Promise<Object|null>} Product object or null if not found
 * @throws {Object} Categorized error object on failure
 *
 * @example
 * const product = await getProductById('product-123');
 * if (product) {
 *   console.log(product.description);
 * }
 */
export async function getProductById(productId) {
    try {
        if (!productId) {
            throw new Error('Product ID is required');
        }

        if (process.env.NODE_ENV !== 'production') {
            console.log('[Endpoints] Fetching product by ID:', productId);
        }

        // Fetch products with ID filter
        const response = await apiClient.get('/products', {
            params: {
                start: 0,
                limit: 1,
                idsSalesPoint: `[${SALES_POINT_ID}]`,
                enabledForChannels: '["SELF_ORDER"]',
                itemListVisibility: 'true',
                ids: `["${productId}"]`,
            },
        });

        const products = response.data.products || [];
        return products.length > 0 ? products[0] : null;
    } catch (error) {
        console.error('[Endpoints] Failed to fetch product by ID:', error);
        throw error;
    }
}

/**
 * Validate category ID format
 * @param {string} categoryId - Category ID to validate
 * @returns {boolean} True if valid
 * @private
 */
function isValidCategoryId(categoryId) {
    return typeof categoryId === 'string' && categoryId.length > 0;
}

/**
 * Validate pagination parameters
 * @param {number} start - Start index
 * @param {number} limit - Limit count
 * @returns {boolean} True if valid
 * @private
 */
function isValidPagination(start, limit) {
    return (
        typeof start === 'number' &&
        typeof limit === 'number' &&
        start >= 0 &&
        limit > 0 &&
        limit <= 1000 // Reasonable upper limit
    );
}

// Export validation functions for testing
export {isValidCategoryId, isValidPagination};
